/**
 * Delta-resync after socket reconnect.
 *
 * The client already tracks the newest `updatedAt` it has seen (via
 * `useSyncStore`). On every non-first socket `connect`, call this to
 * pull only the rows the server has touched since then — faster and
 * cheaper than invalidating the whole list and hitting the paginated
 * endpoint from offset 0.
 */

import type { QueryClient } from '@tanstack/react-query';
import {
  listConversations,
  getMessages,
  type Conversation,
  type ListConversationsResponse,
  type Message,
} from '@/lib/api/conversations';
import {
  useSyncStore,
  SYNC_KEY_CONVERSATIONS,
  syncKeyMessages,
} from '@/stores/sync-store';
import { cacheConversations, cacheMessages } from '@/lib/db';
import {
  mutateMessagesCache,
  appendToNewestPage,
} from '@/lib/utils/messages-cache';
import { compareConversations } from './conversation-sort';

function mergeConversations(
  existing: Conversation[],
  delta: Conversation[]
): Conversation[] {
  if (!delta.length) return existing;
  const byId = new Map(existing.map((c) => [c.id, c]));
  for (const c of delta) byId.set(c.id, c);
  return Array.from(byId.values()).sort(compareConversations);
}

function advanceCursor(key: string, rows: { updatedAt?: string }[]): void {
  let max: string | undefined;
  for (const r of rows) {
    if (r.updatedAt && (!max || r.updatedAt > max)) max = r.updatedAt;
  }
  if (max) useSyncStore.getState().setLastSeen(key, max);
}

export async function resyncConversations(queryClient: QueryClient): Promise<void> {
  const since = useSyncStore.getState().getLastSeen(SYNC_KEY_CONVERSATIONS);

  try {
    const res = await listConversations({
      updatedAfter: since,
      limit: 100,
      sortBy: 'lastMessageAt',
      sortOrder: 'desc',
    });

    if (!res.conversations.length) return;

    advanceCursor(SYNC_KEY_CONVERSATIONS, res.conversations);
    cacheConversations(res.conversations).catch(() => {});

    // Merge into every `['m-conversations', …]` and `['conversations', …]`
    // query in the cache, irrespective of filter params.
    const patch = (q: { queryKey: readonly unknown[] }) => {
      const [root] = q.queryKey;
      return root === 'm-conversations' || root === 'conversations';
    };

    queryClient.setQueriesData<ListConversationsResponse | undefined>(
      { predicate: patch },
      (old) => {
        if (!old) return old;
        const merged = mergeConversations(old.conversations, res.conversations);
        return { ...old, conversations: merged, total: old.total };
      }
    );
  } catch {
    // If the delta call fails, fall back to a plain invalidate so the
    // next render refetches. This matches the pre-delta behavior.
    queryClient.invalidateQueries({ queryKey: ['m-conversations'] });
    queryClient.invalidateQueries({ queryKey: ['conversations'] });
  }
}

export async function resyncMessages(
  queryClient: QueryClient,
  conversationId: string
): Promise<void> {
  const key = syncKeyMessages(conversationId);
  const since = useSyncStore.getState().getLastSeen(key);
  if (!since) {
    // Never seen this conversation live — just invalidate so the next
    // render loads the initial page.
    queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
    return;
  }

  try {
    const res = await getMessages(conversationId, { after: since, limit: 50 });
    if (!res.messages.length) return;

    for (const m of res.messages) {
      if (m.createdAt) {
        useSyncStore.getState().setLastSeen(key, m.createdAt);
      }
    }
    cacheMessages(conversationId, res.messages).catch(() => {});

    mutateMessagesCache(queryClient, conversationId, (pages) => {
      let next = pages;
      for (const m of res.messages) {
        next = appendToNewestPage(next, m as Message);
      }
      return next;
    });
  } catch {
    queryClient.invalidateQueries({ queryKey: ['messages', conversationId] });
  }
}

export function noteConversationUpdate(updatedAt?: string): void {
  if (!updatedAt) return;
  useSyncStore.getState().setLastSeen(SYNC_KEY_CONVERSATIONS, updatedAt);
}

export function noteMessageSeen(conversationId: string, createdAt?: string): void {
  if (!createdAt) return;
  useSyncStore.getState().setLastSeen(syncKeyMessages(conversationId), createdAt);
}
